/**
 * Fetch wrapper. Adds the participant token header, the dev `?event=` override,
 * and turns non-2xx into ApiError with the server's message.
 */
const TOKEN_KEY = 'quest_token'
const EVENT_KEY = 'quest_event'
const IMPERSONATE_KEY = 'quest_impersonate'

export class ApiError extends Error {
  constructor(status, message, data) {
    super(message)
    this.status = status
    this.data = data
  }
}

/**
 * Participant impersonation ("view as") lives in sessionStorage so it is scoped
 * to the tab a super admin opened and never overwrites this browser's own
 * participant token. { token, name, admin } or null.
 */
export function getImpersonation() {
  try { const raw = sessionStorage.getItem(IMPERSONATE_KEY); return raw ? JSON.parse(raw) : null } catch { return null }
}
export function setImpersonation(data) {
  try { data ? sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(data)) : sessionStorage.removeItem(IMPERSONATE_KEY) } catch { /* private mode */ }
}
/**
 * The admin opens `/#imp=<base64url json>`; the fragment never reaches the server
 * or its logs. Consume it once, before the app mounts, and scrub it from the URL.
 */
export function consumeImpersonationHash() {
  try {
    const m = window.location.hash.match(/^#imp=([A-Za-z0-9_-]+)$/)
    if (!m) return
    const bytes = Uint8Array.from(atob(m[1].replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))
    const data = JSON.parse(new TextDecoder().decode(bytes))
    if (data && typeof data.token === 'string') setImpersonation(data)
    history.replaceState(null, '', window.location.pathname + window.location.search)
  } catch { /* malformed: ignore */ }
}
export function encodeImpersonationHash(data) {
  const bytes = new TextEncoder().encode(JSON.stringify(data))
  const b64 = btoa(String.fromCharCode(...bytes))
  return '#imp=' + b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function getToken() {
  const imp = getImpersonation()
  if (imp?.token) return imp.token
  try { return localStorage.getItem(TOKEN_KEY) || '' } catch { return '' }
}
export function setToken(t) {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY) } catch { /* private mode */ }
}

/** Dev override: `?event=slug` in the URL is remembered for the tab's lifetime. */
export function eventOverride() {
  try {
    const q = new URLSearchParams(window.location.search).get('event')
    if (q) sessionStorage.setItem(EVENT_KEY, q)
    return sessionStorage.getItem(EVENT_KEY) || import.meta.env.VITE_DEFAULT_EVENT || ''
  } catch {
    return import.meta.env.VITE_DEFAULT_EVENT || ''
  }
}

const TIMEOUT_MS = 12000

export async function api(path, { method = 'GET', body, query, headers = {}, participant = true, timeout = TIMEOUT_MS } = {}) {
  const url = new URL(path, window.location.origin)
  const ov = eventOverride()
  if (ov && !url.searchParams.has('event')) url.searchParams.set('event', ov)
  for (const [k, v] of Object.entries(query || {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)

  const h = { Accept: 'application/json', ...headers }
  if (body !== undefined) h['Content-Type'] = 'application/json'
  const token = participant ? getToken() : ''
  if (token) h['X-Participant-Token'] = token

  const ac = new AbortController()
  const timer = setTimeout(() => ac.abort(), timeout)
  let res
  try {
    res = await fetch(url.toString(), {
      method,
      headers: h,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
      signal: ac.signal
    })
  } catch (err) {
    clearTimeout(timer)
    // status 0 = never reached the server: offline, DNS, or our own timeout.
    if (err?.name === 'AbortError') throw new ApiError(0, 'Connection timed out. Check your signal and try again.', { timeout: true })
    throw new ApiError(0, "Can't reach the server. Check your signal and try again.", { network: true })
  }
  clearTimeout(timer)
  const text = await res.text()
  let data = null
  try { data = text ? JSON.parse(text) : null } catch { data = { raw: text } }
  if (!res.ok) throw new ApiError(res.status, data?.error || res.statusText || 'Request failed', data)
  return data
}

export const get = (path, opts) => api(path, { ...opts, method: 'GET' })
export const post = (path, body, opts) => api(path, { ...opts, method: 'POST', body })
export const put = (path, body, opts) => api(path, { ...opts, method: 'PUT', body })
export const del = (path, opts) => api(path, { ...opts, method: 'DELETE' })

/**
 * Upload an image for an event (admin only). Reads the File as base64 and
 * posts it to admin-upload, which sniffs the bytes and stores it on S3.
 * Resolves to the public URL to save in logo_url etc.
 */
export function uploadImage(file, { eventId, kind = 'booth-logo' }) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new ApiError(0, 'Could not read that file'))
    reader.onload = () => {
      post('/api/admin-upload', { kind, data: reader.result }, { query: { event_id: eventId }, participant: false, timeout: 30000 })
        .then((res) => resolve(res.url), reject)
    }
    reader.readAsDataURL(file)
  })
}
